<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="page">
	<div class="page-header">
		<div class="back">
			<globalictcc-button
				label="Back to scenario"
				[textOnly]="true"
				iconName="arrow-left"
				(handleClick)="onGoBack()"
			/>
		</div>

		<div class="page-title">
			<span class="page-name">Configuration</span>
		</div>
	</div>

	<form
		[formGroup]="scenarioAndLayerForm"
		class="filters"
	>
		<globalictcc-dropdown
			label="Scenario"
			formControlName="scenario"
			[options]="scenariosOptions"
		/>

		<globalictcc-dropdown
			label="Layer"
			formControlName="layer"
			[options]="layersOptions"
		/>

		<globalictcc-button
			label="Select"
			(handleClick)="onSelect()"
		/>
	</form>

	<form [formGroup]="configurationForm">
		<div class="configurations">
			<globalictcc-accordion-group>
				<globalictcc-accordion
					title="Spatial Shifting"
					[tooltip]="spatialShiftingTooltip"
				>
					<div class="spatial-shift">
						<span class="accordion-description">
							Hover over the countries in the map to check their Carbon intensity.
							Select a possible country for your running software to view seasonal and
							annual emissions.
						</span>

						<globalictcc-dropdown
							label="Country"
							formControlName="country"
							class="fit"
							[options]="countriesOptions"
						/>

						<globalictcc-map
							[isTitleVisible]="false"
							[data]="spatialShiftingMapChart?.chart"
							[isLoading]="isSpatialShiftingMapChartLoading"
							[hasError]="spatialShiftingMapChart?.hasError"
							[configOverrides]="mapConfigOverrides"
							(handleMapRefresh)="onMapRefresh()"
							(handleChartInit)="onMapInit($event)"
							(handleChartClick)="onMapClick($event)"
						/>

						<div class="carbon-intensity">
							<span class="carbon-intensity-title">
								Regional carbon intensity:
								{{
									configurationForm.controls.country.value
										? regionsMap[configurationForm.controls.country.value]
										: "No country selected"
								}}

								<span
									class="icon-warning"
									[ngxTippy]="regionalCarbonIntensityTooltip"
								></span>
							</span>

							@if (spatialShiftingData) {
								<globalictcc-kpi
									[label]="spatialShiftingData.annualCI.label"
									[value]="spatialShiftingData.annualCI.carbonIntensity.value"
									[unit]="spatialShiftingData.annualCI.carbonIntensity.unit"
								/>

								<div class="seasonal-carbon-intensity">
									@for (quarter of spatialShiftingData.quarterCIs; track $index) {
										<globalictcc-kpi
											[label]="quarter.label"
											[value]="quarter.carbonIntensity.value"
											[unit]="quarter.carbonIntensity.unit"
										/>
									}
								</div>
							} @else {
								<div class="no-regional-data">
									<span class="icon-warning-hex"></span>

									<span class="message">No data available</span>
								</div>
							}
						</div>
					</div>
				</globalictcc-accordion>

				@if (isSingleLayerEdit) {
					<globalictcc-accordion
						title="Temporal Shifting - CPU"
						[tooltip]="cpuTemporalShiftingTooltip"
					>
						<div class="temporal-filters">
							<globalictcc-input
								label="Morning"
								inputType="number"
								unit="%"
								formControlName="morningShiftCPU"
								[min]="1"
								[max]="100"
							/>

							<globalictcc-input
								label="Afternoon"
								inputType="number"
								unit="%"
								formControlName="afternoonShiftCPU"
								[min]="1"
								[max]="100"
							/>

							<globalictcc-input
								label="Evening"
								inputType="number"
								unit="%"
								formControlName="eveningShiftCPU"
								[min]="1"
								[max]="100"
							/>

							<globalictcc-input
								label="Night"
								inputType="number"
								unit="%"
								formControlName="nightShiftCPU"
								[min]="1"
								[max]="100"
							/>
						</div>

						<globalictcc-chart
							chartType="line"
							[isTitleVisible]="false"
							[data]="temporalShiftingLineChart?.chart"
							[isLoading]="isTemporalShiftingLineChartLoading"
							[hasError]="temporalShiftingLineChart?.hasError"
							[configOverrides]="lineOverrides"
							(handleChartRefresh)="onTemporalShiftingChartRefresh()"
						/>
					</globalictcc-accordion>

					<globalictcc-accordion
						title="Temporal Shifting - GPU"
						[tooltip]="gpuTemporalShiftingTooltip"
					>
						<div class="temporal-filters">
							<globalictcc-input
								label="Morning"
								inputType="number"
								unit="%"
								formControlName="morningShiftGPU"
								[min]="0"
								[max]="100"
							/>

							<globalictcc-input
								label="Afternoon"
								inputType="number"
								unit="%"
								formControlName="afternoonShiftGPU"
								[min]="0"
								[max]="100"
							/>

							<globalictcc-input
								label="Evening"
								inputType="number"
								unit="%"
								formControlName="eveningShiftGPU"
								[min]="0"
								[max]="100"
							/>

							<globalictcc-input
								label="Night"
								inputType="number"
								unit="%"
								formControlName="nightShiftGPU"
								[min]="0"
								[max]="100"
							/>
						</div>

						<globalictcc-chart
							chartType="line"
							[isTitleVisible]="false"
							[configOverrides]="lineOverrides"
							[data]="temporalShiftingLineChart?.chart"
							[isLoading]="isTemporalShiftingLineChartLoading"
							[hasError]="temporalShiftingLineChart?.hasError"
							(handleChartRefresh)="onTemporalShiftingChartRefresh()"
						/>
					</globalictcc-accordion>
				}

				<globalictcc-accordion
					title="PUE Configuration"
					[tooltip]="pueConfigurationTooltip"
				>
					<div class="checkbox">
						<globalictcc-checkbox
							(handleCheckboxChange)="onCloudApplicationChange($event)"
							[checked]="isCloudApplication"
						/>

						<span>Cloud application</span>
					</div>

					@if (isCloudApplication) {
						<globalictcc-dropdown
							label="Cloud Provider"
							formControlName="cloudProvider"
							[options]="cloudProvidersOptions"
							class="fit"
						/>
					} @else {
						<div class="pue">
							<globalictcc-input
								label="PUE"
								formControlName="pue"
								inputType="number"
								[min]="1"
								[max]="3"
								[step]="0.01"
							/>

							<span>Default PUE: 1.67</span>
						</div>
					}
				</globalictcc-accordion>
			</globalictcc-accordion-group>

			<div class="bottom-actions">
				<globalictcc-button
					label="Cancel"
					color="secondary"
					(handleClick)="onGoBack()"
				/>

				<globalictcc-button
					label="Apply all configuration"
					[disabled]="!configurationForm.valid"
					(handleClick)="onApplyConfiguration()"
				/>
			</div>
		</div>
	</form>
</section>

<!-- Tooltips templates -->

<ng-template #spatialShiftingTooltip>
	<span class="tooltip-title">Spatial Shifting</span>

	<span>
		Spatial Shifting means moving your computation to another physical location where the
		current carbon intensity is lower.
		<br />
		It might be a region that naturally has lower carbon sources of energy. Through the map you
		can view geographic regions differentiated by carbon intensity and select the one for which
		you would like to predict a new scenario by adopting spatial shifting operations
	</span>
</ng-template>

<ng-template #regionalCarbonIntensityTooltip>
	<span class="tooltip-title">Regional carbon intensity</span>
	<span>
		Carbon intensity measures how much carbon (gCO2e) is emitted per kilowatt-hour(kWh) of
		electricity consumed. The standard unit of carbon intensity is gCO2eq/kWh, or grams of
		carbon per kilowatt hour.
		<br />
		Carbon intensity varies by location since some regions have an energy mix containing more
		clean energy sources than others. With the graph you can look at future carbon intensity
		values in the selected region to understand its overall trend over time.
	</span>
</ng-template>

<ng-template #cpuTemporalShiftingTooltip>
	<span class="tooltip-title">Temporal Shifting - CPU</span>

	<span>
		Temporal Shifting means moving your computation to another time, i.e. later in the day or
		night when it’s sunnier or windier and, therefore, the carbon intensity is lower.
		<br />
		Moving your operation involves time-shifting the workload whenever possible. This load
		impacts several resources, including the CPU. The graph allows you to view the average
		carbon intensity trend throughout the day. Through this, it is possible to get an idea of
		how to make the shift: by changing the percentages at the bottom, it is possible to move
		part of the workload from one time of the day at higher carbon intensity to reduce the
		environmental impact of the application.
	</span>
</ng-template>

<ng-template #gpuTemporalShiftingTooltip>
	<span class="tooltip-title">Temporal Shifting - GPU</span>

	<span>
		Temporal Shifting means moving your computation to another time, i.e. later in the day or
		night when it’s sunnier oe windier and, therefore, the carbon intensity is lower.
		<br />
		Moving your operation involves time-shifting the workload whenever possible. this load
		impacts several resources, including the GPU. The graph allows you to view the average
		carbon intensity trend throughout the day. Through this, it is possible to get an idea of
		how to make the shift: by changing the percentages at the bottom, it is possible to move
		part of the workload from one time of the day at higher carbon intensity to reduce the
		environmental impact of the application.
	</span>
</ng-template>

<ng-template #pueConfigurationTooltip>
	<span class="tooltip-title">PUE Configuration</span>

	<span>
		Power usage Effectiveness (PUE) is a metric developed by Green Grid in 2006 to measure data
		centre energy efficiency. Specially, this relates to how much energy the computing equipment
		uses as compared to cooling and other overheads supporting the equipment. A data centre’s
		PUE close to 1.0 means that computing is using nearly all energy. When the PUE is 2.0, this
		means an additional watt of IT power is required to cool and distribute power to the IT
		equipment for every watt of IT power it uses.
		<br />
		<ul>
			<li>
				If your Data centre is On-premise or you want to consider running On premise
				software, you can choose to manually enter the value of the relevant PUE.
			</li>

			<li>
				If you use the Cloud or want to evaluate the possibility of moving software to the
				Cloud, you can select the Cloud Provider from the options. Each already has a
				publicly declared PUE associated with its Data Centres.
			</li>
		</ul>
	</span>
</ng-template>
